import React, { useMemo } from 'react';
import { Alert, Card, Descriptions, Empty, Progress, Space, Table, Tag, Tooltip, Typography } from 'antd';
import type {
  ScenarioEconomyApplied,
  ScenarioEconomyRejected,
  ScenarioEconomyState,
} from '../../api/seasonsAdminApi';

const { Text } = Typography;

/**
 * Экономика ПРОХОЖДЕНИЯ в отладочной консоли
 * (RFC_SCENARIO_AUTHORING_ARCHITECTURE §4).
 *
 * Панель отвечает на два вопроса автора, на которые снимок мира не отвечает:
 *
 *  1. Хватило ли денег на решение, которое задумано как выбор? Стартовый баланс,
 *     выручка и траты показаны рядом, потому что «купить укрытие» имеет смысл
 *     только если продажа его окупает — и это видно числами, а не намерением.
 *  2. ПОЧЕМУ команда не сработала? Отказ движка — не исключение и не пропавшее
 *     действие: он приходит строкой с кодом. Без этого списка автор видел бы
 *     лишь «баланс не изменился» и искал бы ошибку в симуляции.
 */

const REJECTION_HINTS: Record<string, string> = {
  sell_nothing_in_storage: 'На утро этого дня склад пуст: продать можно только собранное по ПРЕДЫДУЩИЙ день включительно.',
  sell_exceeds_storage: 'Запрошено больше, чем лежит на складе. Команда отклонена целиком — частично исполнять заказ, которого игрок не делал, нельзя.',
  sell_crop_not_priced: 'Релиз не покупает эту культуру: добавьте цену в economy.prices.sell.',
  buy_insufficient_funds: 'Денег не хватило. Либо цена высока для этой точки сценария, либо продажа должна случиться раньше.',
  buy_out_of_stock: 'Ассортимент релиза исчерпан (stock). Это балансный потолок, а не ошибка игрока.',
  install_item_not_owned: 'Укрытие не куплено. Покупка и установка — два отдельных перехода.',
  install_window_in_past: 'Окно укрытия начинается раньше открытого дня: прошлое не переигрывается.',
  install_window_too_long: 'Окно длиннее, чем позволяет maxWindowDays предмета.',
  install_window_unbounded: 'У предмета есть maxWindowDays, значит установка обязана иметь дату конца.',
  install_covering_conflict: 'Грядка уже укрыта в пересекающемся окне.',
  install_unknown_bed: 'Такой грядки в этом мире нет.',
  command_id_conflict: 'Этот id уже применён с ДРУГИМ телом. Доставку можно повторять, историю переписывать нельзя.',
  command_unknown_type: 'Неизвестный тип команды.',
};

const money = (value: number, currency: string) => `${value} ${currency}`;

const kgRows = (map: Record<string, number>) => Object.entries(map)
  .filter(([, kg]) => kg > 0)
  .map(([crop, kg]) => `${crop} ${kg.toFixed(3)} кг`);

const describeApplied = (row: ScenarioEconomyApplied): string => {
  switch (row.type) {
    case 'sell_harvest':
      return `продал ${row.crop} ${row.quantityKg?.toFixed(3)} кг по ${row.unitPrice}/кг`;
    case 'buy_item':
      return `купил ${row.itemId} ×${row.quantity}`;
    case 'install_covering':
      return `укрыл ${row.bedId}: ${row.activeFrom} — ${row.activeTo || 'без конца'}`;
    default:
      return row.type;
  }
};

export const ScenarioEconomyPanel: React.FC<{
  economy: ScenarioEconomyState | null;
  specErrors?: Array<{ code: string; path: string; message: string }>;
}> = ({ economy, specErrors = [] }) => {
  const storage = useMemo(() => (economy ? kgRows(economy.storageKg) : []), [economy]);
  const sold = useMemo(() => (economy ? kgRows(economy.soldKg) : []), [economy]);

  if (specErrors.length > 0) {
    return (
      <Card size="small" title="Экономика прохождения">
        <Alert
          type="error"
          showIcon
          message="Блок economy не прочитан"
          description={(
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {specErrors.map((error) => (
                <li key={`${error.code}:${error.path}`}>
                  <Text code>{error.path}</Text> {error.message}
                </li>
              ))}
            </ul>
          )}
        />
      </Card>
    );
  }

  if (!economy) {
    return (
      <Card size="small" title="Экономика прохождения">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="У сценария нет блока economy. Это умолчание: большинство заданий играются на ограниченных запасах без денег."
        />
      </Card>
    );
  }

  const net = economy.balance - economy.startingBalance;

  return (
    <Card
      size="small"
      title={(
        <Space wrap>
          Экономика прохождения
          <Tag color={economy.balance > 0 ? 'green' : 'default'}>
            {money(economy.balance, economy.currency)}
          </Tag>
          <Text type="secondary">
            старт {economy.startingBalance} · выручка {economy.revenue} · траты {economy.spent}
            {net !== 0 ? ` · итог ${net > 0 ? '+' : ''}${net}` : ''}
          </Text>
        </Space>
      )}
    >
      {economy.batteryExhausted && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`Ресурсная батарейка кончилась на ${economy.stoppedAtDate}`}
          description="Календарь встал раньше дедлайна: попытка не дошла до конца задачи и терминалом не считается. Либо запас мал для задуманного пути, либо цена дня высока."
        />
      )}

      <Descriptions size="small" column={2} bordered style={{ marginBottom: 12 }}>
        <Descriptions.Item label="На складе">
          {storage.length ? storage.join(', ') : <Text type="secondary">пусто</Text>}
        </Descriptions.Item>
        <Descriptions.Item label="Продано">
          {sold.length ? sold.join(', ') : <Text type="secondary">ничего</Text>}
        </Descriptions.Item>
        <Descriptions.Item label="Прайс" span={2}>
          {Object.keys(economy.sellPrices).length
            ? Object.entries(economy.sellPrices).map(([crop, price]) => `${crop} ${price}/кг`).join(' · ')
            : <Text type="secondary">ничего не покупают</Text>}
        </Descriptions.Item>
        {economy.battery && (
          <Descriptions.Item label="Батарейка (ресурс)" span={2}>
            <Space>
              <Progress
                percent={Math.round((economy.battery.charge / economy.battery.capacity) * 100)}
                size="small"
                style={{ width: 160 }}
                status={economy.batteryExhausted ? 'exception' : 'normal'}
              />
              <Text type="secondary">
                {economy.battery.charge} / {economy.battery.capacity} · день стоит {economy.battery.costPerDay}
                {economy.battery.regenPerDay > 0 ? ` · +${economy.battery.regenPerDay}/день` : ''}
              </Text>
            </Space>
          </Descriptions.Item>
        )}
      </Descriptions>

      {economy.catalog.length > 0 && (
        <Table
          rowKey={(item) => item.id}
          size="small"
          pagination={false}
          style={{ marginBottom: 12 }}
          dataSource={economy.catalog}
          columns={[
            { title: 'Предмет', dataIndex: 'title' },
            { title: 'Вид', dataIndex: 'kind', width: 90, render: (kind: string) => <Tag>{kind}</Tag> },
            { title: 'Цена', dataIndex: 'price', width: 80 },
            {
              title: 'Ассортимент',
              dataIndex: 'stock',
              width: 110,
              render: (stock: number | null) => (stock == null ? '∞' : stock),
            },
            {
              title: 'Куплено / на руках',
              key: 'owned',
              width: 150,
              render: (_, item) => `${item.bought} / ${item.owned}`,
            },
            {
              title: 'Окно',
              dataIndex: 'maxWindowDays',
              width: 90,
              render: (days: number | null) => (days == null ? '—' : `≤ ${days} д`),
            },
          ]}
        />
      )}

      <Table<ScenarioEconomyApplied>
        rowKey={(row, index) => `${row.id || 'cmd'}-${index}`}
        size="small"
        pagination={false}
        title={() => <Text strong>Применённые команды</Text>}
        locale={{ emptyText: 'Команд не было' }}
        dataSource={economy.applied}
        columns={[
          { title: 'День', dataIndex: 'date', width: 110 },
          { title: 'Что произошло', key: 'what', render: (_, row) => describeApplied(row) },
          {
            title: 'Сумма',
            dataIndex: 'amount',
            width: 90,
            render: (amount?: number) => (amount == null
              ? '—'
              : <Text type={amount >= 0 ? 'success' : 'danger'}>{amount > 0 ? `+${amount}` : amount}</Text>),
          },
          { title: 'Баланс', dataIndex: 'balanceAfter', width: 90 },
        ]}
      />

      {economy.rejected.length > 0 && (
        <Table<ScenarioEconomyRejected>
          rowKey={(row, index) => `${row.id || 'rej'}-${index}`}
          size="small"
          pagination={false}
          style={{ marginTop: 12 }}
          title={() => <Text strong type="danger">Отклонённые команды</Text>}
          dataSource={economy.rejected}
          columns={[
            { title: 'День', dataIndex: 'date', width: 110 },
            { title: 'Команда', dataIndex: 'type', width: 150 },
            {
              title: 'Причина',
              key: 'why',
              render: (_, row) => (
                <Tooltip title={REJECTION_HINTS[row.code] || row.message}>
                  <Space direction="vertical" size={0}>
                    <Tag color="red">{row.code}</Tag>
                    <Text type="secondary">{REJECTION_HINTS[row.code] || row.message}</Text>
                  </Space>
                </Tooltip>
              ),
            },
          ]}
        />
      )}

      {economy.unscheduled.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 12 }}
          message={`${economy.unscheduled.length} команд без даты — реплей их не увидел`}
        />
      )}
    </Card>
  );
};

export default ScenarioEconomyPanel;
